import { isViewerOrLedUser } from '@modules/_shared/utils/isViewerOrLedUser';
import { isOwnerInitiatedLeadAgentSource } from '@modules/_shared/utils/leadAgentSources';

type LeadAgentWithUserAndSource = {
  source?: string | null;
  user: {
    id: string;
  } | null;
};

export type MergedLeadAgent<T extends LeadAgentWithUserAndSource> = T & {
  sources: NonNullable<T['source']>[];
};

/**
 * Returns the lead agents visible to a viewer, with one row per user.
 *
 * A user can have both an owner-initiated (result page, prospection map) and
 * a CRM/automation assignment row. The latter is the authoritative row for
 * assignment data, while `sources` keeps the complete provenance for display.
 * Pass rows ordered by `created_at` so ties resolve deterministically.
 */
export const mergeVisibleLeadAgents = <T extends LeadAgentWithUserAndSource>(
  leadAgents: readonly T[] | null | undefined,
  viewerId: string | null | undefined,
  ledTeamUserIds: readonly string[] | null | undefined,
): MergedLeadAgent<T>[] => {
  const mergedAgentsByUserId = new Map<string, MergedLeadAgent<T>>();

  for (const agent of leadAgents ?? []) {
    const userId = agent.user?.id;
    if (
      userId == null ||
      !isViewerOrLedUser(viewerId, ledTeamUserIds, userId)
    ) {
      continue;
    }

    const existingAgent = mergedAgentsByUserId.get(userId);
    if (existingAgent == null) {
      mergedAgentsByUserId.set(userId, {
        ...agent,
        sources: agent.source == null ? [] : [agent.source],
      });
      continue;
    }

    // Owner-initiated rows record provenance; a later CRM/automation
    // assignment for the same user is the authoritative row for stage and
    // assignment data.
    if (
      isOwnerInitiatedLeadAgentSource(existingAgent.source) &&
      !isOwnerInitiatedLeadAgentSource(agent.source)
    ) {
      mergedAgentsByUserId.set(userId, {
        ...agent,
        sources:
          agent.source == null
            ? existingAgent.sources
            : [...existingAgent.sources, agent.source],
      });
    } else if (
      agent.source != null &&
      !existingAgent.sources.includes(agent.source)
    ) {
      existingAgent.sources.push(agent.source);
    }
  }

  return Array.from(mergedAgentsByUserId.values());
};
